// FILE: src/components/app/bases/UploadDataComponent/utils/applyMapping.js

export const FRIENDLY_ORDER = [
  "ID",
  "Date",
  "Description",
  "Currency",
  "Type",
  "Account",        // for non-transfers
  "From Account",   // for transfers
  "To Account",     // for transfers
  "Amount",
  "Tags",
];

// System-path -> friendly header (and synonyms)
const KEY_SYNONYMS = new Map([
  // system paths
  ["id", "ID"],
  ["date", "Date"],
  ["description", "Description"],
  ["amount", "Amount"],
  ["currencycode", "Currency"],
  ["typename", "Type"],
  ["tagpath", "Tags"],
  ["accountname", "Account"],

  // NEW: transfer endpoints
  ["fromaccount", "From Account"],
  ["toaccount", "To Account"],
  ["from account", "From Account"],
  ["to account", "To Account"],
  ["account from", "From Account"],
  ["account to", "To Account"],
  ["from", "From Account"],
  ["to", "To Account"],

  // common variants
  ["txn id", "ID"], ["transaction id", "ID"],
  ["transaction date", "Date"], ["posted date", "Date"],
  ["memo", "Description"], ["details", "Description"], ["payee", "Description"],
  ["currency", "Currency"], ["currency code", "Currency"], ["ccy", "Currency"],
  ["type", "Type"], ["transaction type", "Type"],
  ["account", "Account"], ["account name", "Account"],

  // inflow/outflow variants still map to Amount (sign handled downstream)
  ["value", "Amount"], ["debit", "Amount"], ["credit", "Amount"], ["outflow", "Amount"], ["inflow", "Amount"],

  ["tags", "Tags"], ["tag paths", "Tags"], ["categories", "Tags"], ["category", "Tags"], ["labels", "Tags"],
]);

function toFriendlyKey(k) {
  const raw = String(k ?? "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  // exact friendly?
  const eq = FRIENDLY_ORDER.find((h) => h.toLowerCase() === lower);
  if (eq) return eq;
  // via synonyms / system path
  return KEY_SYNONYMS.get(lower) || "";
}

export function applyMappingToRows(rawHeaders, rawRows, mappingsArg = [], defaults = {}) {
  const headerToIndex = new Map(rawHeaders.map((h, i) => [String(h), i]));
  const lowerToIndex  = new Map(rawHeaders.map((h, i) => [String(h).toLowerCase(), i]));

  const resolveIndex = (sel) => {
    if (sel == null || sel === "") return -1;

    // number index
    if (typeof sel === "number") return sel >= 0 ? sel : -1;

    // object shapes we might get from external UIs
    if (typeof sel === "object") {
      if (Number.isFinite(sel.index)) return sel.index;
      if (Number.isFinite(sel.headerIndex)) return sel.headerIndex;
      if (Number.isFinite(sel.col)) return sel.col;
      if (sel.header && headerToIndex.has(sel.header)) return headerToIndex.get(sel.header);
      if (sel.name && headerToIndex.has(sel.name)) return headerToIndex.get(sel.name);
      const lh = String(sel.header ?? sel.name ?? "").toLowerCase();
      if (lowerToIndex.has(lh)) return lowerToIndex.get(lh);
      return -1;
    }

    // string header
    const s = String(sel);
    if (headerToIndex.has(s)) return headerToIndex.get(s);
    const ls = s.toLowerCase();
    if (lowerToIndex.has(ls)) return lowerToIndex.get(ls);
    const iFz = rawHeaders.findIndex((h) => String(h).toLowerCase().includes(ls));
    return iFz >= 0 ? iFz : -1;
  };

  // --- Normalize mappingsArg into: friendlyTarget -> [sourceHeader | index | selectorObj] ---
  const normMap = {};

  // Case A: modal array [{ sourceHeader, targetPath }]
  if (Array.isArray(mappingsArg)) {
    for (const m of mappingsArg) {
      if (!m || m.targetPath === "IGNORE") continue;
      const friendly = toFriendlyKey(m.targetPath);
      if (!friendly) continue;
      if (!normMap[friendly]) normMap[friendly] = [];
      // Prefer the actual uploaded header name as selector
      normMap[friendly].push(m.sourceHeader);
    }
  } else {
    // Case B: legacy object { targetKey: selector | selector[] }
    for (const [key, sel] of Object.entries(mappingsArg || {})) {
      const friendly = toFriendlyKey(key);
      if (!friendly) continue;
      if (!normMap[friendly]) normMap[friendly] = [];
      if (Array.isArray(sel)) normMap[friendly].push(...sel);
      else normMap[friendly].push(sel);
    }
  }

  const displayHeaders = [...FRIENDLY_ORDER];

  const displayRows = rawRows.map((row) => {
    const out = [];

    for (const tgt of FRIENDLY_ORDER) {
      const sel = normMap[tgt];

      // Tags can combine multiple sources
      if (tgt === "Tags") {
        const sels = Array.isArray(sel) ? sel : (sel != null ? [sel] : []);
        const cells = sels
          .map((s) => {
            const j = resolveIndex(s);
            return j >= 0 ? row[j] : "";
          })
          .filter((v) => v != null && String(v).trim() !== "");

        const normalized = cells.map(normalizeTagCellForStorage).filter(Boolean);
        out.push(normalized.join(" | "));
        continue;
      }

      // Others: take first mapped source (if multiple provided)
      const sources = Array.isArray(sel) ? sel : (sel != null ? [sel] : []);
      let val = "";
      for (const s of sources) {
        const j = resolveIndex(s);
        if (j >= 0) {
          val = row[j];
          if (val != null && String(val).trim() !== "") break; // prefer first non-empty
        }
      }
      out.push(val ?? "");
    }

    return out;
  });

  const mappedTargets = new Set(Object.keys(normMap));

  return {
    displayHeaders,
    displayRows,
    mappedTargets,
    defaults: defaults || {},
  };
}

/**
 * Keep each hierarchical path intact and canonicalize spacing.
 * - Split multiple paths on comma / pipe / semicolon (with escape support)
 * - Keep "/" inside a path and normalize it to " / "
 * - Collapse internal spaces
 */
function normalizeTagCellForStorage(cell) {
  if (cell == null) return "";
  const s = String(cell);

  const paths = [];
  let buf = "";
  let i = 0;

  const isMultiSep = (ch) => ch === "," || ch === "|" || ch === ";";

  while (i < s.length) {
    const ch = s[i];

    // Escape: keep literal separator or slash
    if (ch === "\\" && i + 1 < s.length) {
      const next = s[i + 1];
      if (isMultiSep(next) || next === "/" || next === "\\") {
        buf += next;
        i += 2;
        continue;
      }
    }

    if (isMultiSep(ch)) {
      const token = buf.trim();
      if (token) paths.push(token);
      buf = "";
      i++;
      while (i < s.length && s[i] === " ") i++;
      continue;
    }

    buf += ch;
    i++;
  }

  const last = buf.trim();
  if (last) paths.push(last);

  return paths
    .map((p) => p.replace(/\s*\/\s*/g, " / "))
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(" | ");
}
